import { ApiError, isRetryable, request } from '../api/client'
import { getDeviceToken, getPlayerId } from '../stores/device'
import { mintEventId } from './eventId'
import type { OutboxItem, OutboxKind, OutboxStore } from './types'

/**
 * The outbox — offline-sync.md §1, §4, §5.
 *
 * > **Gameplay never awaits the network.** Every event and progress write is
 * > enqueued locally and returns immediately. The network is a background
 * > concern, always.
 *
 * *"This is not an optimisation. A 4-year-old mid-jump cannot wait for a
 * 3-second timeout, and a teacher running a 30-minute slot cannot debug wifi."*
 */

/** offline-sync.md §5, all four numbers. */
const MAX_ATTEMPTS = 10
const QUEUE_CAP = 500
/** How long one child's queued work waits on a shared device for them to sign in again — see `nextFor`. */
const HOLD_MAX_MS = 14 * 24 * 60 * 60 * 1000
const BACKOFF_BASE_MS = 1_000
const BACKOFF_CAP_MS = 60_000
/**
 * Full jitter's lower bound. Small next to the first 1 s window so it barely
 * narrows the spread, and present so a draw of ~0 is never an immediate retry
 * into the failure that just happened.
 */
const BACKOFF_FLOOR_MS = 250
/** §4 — "periodic timer (30 s) **while items remain**". */
const PERIODIC_MS = 30_000

/** The route a queued event is addressed to, and the one that takes many. */
const EVENTS_PATH = '/events'
const EVENTS_BATCH_PATH = '/events/batch'

/**
 * The most events one `POST /events/batch` carries — the API's own ceiling
 * (`EVENT_BATCH_MAX` in create-event.dto.ts), never exceeded from here.
 */
export const EVENT_BATCH_MAX = 100

/**
 * A batch's body budget, in characters of JSON.
 *
 * ⚠️ BELOW THE API'S BODY LIMIT, NOT AT IT. Nest's JSON parser refuses anything
 * over 100 KB with a 413 before validation runs. A hundred legal events are
 * well under this — every field is bounded — so the budget only ever binds on
 * an item that was never going to validate, which then travels alone and is
 * refused alone. Characters stand in for bytes because a legal event is ASCII:
 * enums, slugs, UUIDs, numbers and an ISO timestamp.
 */
const EVENT_BATCH_MAX_CHARS = 90 * 1024

/** What `clientEventId` adds to a body that has not been given one yet. */
const EVENT_ID_CHARS = 64

/**
 * 🔴 THE I1 GUARD, AT THE LAST POINT ON THE DEVICE WHERE IT IS STILL POSSIBLE.
 *
 * By the time §4.2's DTO whitelist rejects a landmark-shaped payload, the data
 * has already **left the device** — and I1 is about transmission: *"nothing
 * derived from a child's face or body is ever transmitted, persisted, or
 * logged"* (privacy-compliance.md §2). The server's 400 protects the database;
 * it cannot protect the invariant.
 *
 * Every other guard in the stack is upstream of a network call —
 * `check-no-exfil.mjs` bans `fetch` inside `vision/`, and the §3.1 contract check
 * stops a game reaching the network. Those work because games *cannot* send
 * anything. This module is the thing that sends, so the check belongs here.
 */
const FORBIDDEN_KEYS =
  /^(landmarks?|blendshapes?|baseline|poseSignals|worldLandmarks|keypoints|embedding|frame|frames|imageData|faceLandmarks|handLandmarks)$/i

function assertNoBodyDerivedFields(body: unknown, path = 'body'): void {
  if (typeof body !== 'object' || body === null) return

  for (const [key, value] of Object.entries(body as Record<string, unknown>)) {
    if (FORBIDDEN_KEYS.test(key)) {
      throw new Error(
        `I1 violation: "${path}.${key}" looks body-derived and must never leave the device ` +
          `(ENGINEERING-GUIDE.md §0, privacy-compliance.md §2).`,
      )
    }
    if (typeof value === 'object' && value !== null) {
      assertNoBodyDerivedFields(value, `${path}.${key}`)
    }
  }
}

/**
 * How long to wait after the `cycle`-th failed flush in a row — AWS's "full
 * jitter": a uniform draw between the floor and `min(CAP, BASE · 2^(cycle−1))`.
 *
 * 🔴 RANDOM, BECAUSE A CLASSROOM FAILS TOGETHER. Thirty laptops lose the same
 * wifi at the same instant, so a deterministic 1 s → 2 s → 4 s schedule kept all
 * thirty in lockstep for as long as the outage lasted, and every retry the API
 * saw was thirty at once — the moment it came back included. Drawing the wait
 * from the whole window spreads them across it; the ceiling still doubles per
 * cycle and still stops at the §5 cap, so a long outage is still a retry about
 * once a minute, on average every thirty seconds.
 *
 * `cycle` counts failed CYCLES, not attempts on one item — the backoff is per
 * cycle (§5).
 */
export function fullJitterBackoff(cycle: number, random: () => number = Math.random): number {
  // The exponent is clamped long before 2^n could overflow to Infinity.
  const ceiling = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** Math.min(Math.max(cycle - 1, 0), 16))
  const draw = Math.min(Math.max(random(), 0), 1)
  return BACKOFF_FLOOR_MS + draw * (ceiling - BACKOFF_FLOOR_MS)
}

export interface Clock {
  now(): number
  setTimeout(fn: () => void, ms: number): number
  clearTimeout(handle: number): void
}

export const systemClock: Clock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number,
  clearTimeout: (handle) => {
    globalThis.clearTimeout(handle)
  },
}

export interface OutboxDeps {
  store: OutboxStore
  clock?: Clock
  /** Injectable so the policy tests never touch `fetch`. */
  send?: (item: OutboxItem, token: string, playerId: string | null) => Promise<void>
  /**
   * Two or more consecutive queued events in one request. Resolves only when
   * the API answered 2xx for the whole batch, which is what lets the outbox
   * delete exactly the items it sent.
   */
  sendBatch?: (items: readonly OutboxItem[], token: string) => Promise<void>
  getToken?: () => string | null
  getPlayer?: () => string | null
  /** Injectable so the jitter is testable; `Math.random` otherwise. */
  random?: () => number
}

export interface EnqueueInput {
  kind: OutboxKind
  path: string
  body: unknown
  /** Defaults to now. Passed explicitly when replaying a recorded moment. */
  createdAt?: number
}

/** What §4.7's teacher chip reads. The child is never shown any of it (I6). */
export interface OutboxStats {
  queued: number
  failed: number
  /** Items refused before they were ever written — I1, and a real incident. */
  refused: number
  /** True while a flush cycle is in flight — offline-sync.md §7's "flushing". */
  sending: boolean
}

const EMPTY_STATS: OutboxStats = { queued: 0, failed: 0, refused: 0, sending: false }

/** Whether a delivery let the FIFO loop carry on, or ended the cycle. */
type Step = 'continue' | 'stop'

export function createOutbox(deps: OutboxDeps) {
  const {
    store,
    clock = systemClock,
    send = sendOverHttp,
    sendBatch = sendBatchOverHttp,
    getToken = getDeviceToken,
    getPlayer = getPlayerId,
    random = Math.random,
  } = deps

  /** Serialises writes so FIFO survives concurrent `enqueue()` calls. */
  let tail: Promise<unknown> = Promise.resolve()
  let flushing = false
  let consecutiveFailedCycles = 0
  let backoffUntil = 0
  let timer: number | null = null
  /** When `timer` fires — so a wake-up already due sooner is never pushed back. */
  let timerAt = 0
  let refused = 0
  /**
   * How many events the next batch may carry. Starts at the ceiling, shrinks to
   * a quarter of a batch that failed retryably, doubles back on every success —
   * see `retryLater` for why it moves at all.
   */
  let eventBatchLimit = EVENT_BATCH_MAX

  /** The signed-in child, stamped on an item as it is queued. Nothing before the device has a player. */
  function ownerNow(): Pick<OutboxItem, 'owner'> {
    const playerId = getPlayer()
    return playerId ? { owner: playerId } : {}
  }

  /**
   * The next items the signed-in child may send, oldest first.
   *
   * 🔴 ANOTHER CHILD'S ITEMS ARE SKIPPED, NOT SENT AND NOT DROPPED. They go when
   * that child signs in on this device again; order still holds within each
   * child, which is the only order the API depends on (session, then its
   * events, then the snapshot). One left longer than `HOLD_MAX_MS` is
   * discarded: their token has long gone from this device, and a queue that
   * only ever grows ends at the size cap dropping the current child's work.
   */
  async function nextFor(player: string | null): Promise<OutboxItem[]> {
    const window = await store.head(eventBatchLimit)
    const mine = (item: OutboxItem) => item.owner === undefined || item.owner === player
    if (window.every(mine)) return window

    const queued = await store.all()
    for (const item of queued) {
      if (mine(item) || item.id === undefined || clock.now() - item.createdAt <= HOLD_MAX_MS) continue
      console.warn(`outbox: discarding another child's ${item.kind}, held too long`)
      await store.remove(item.id)
    }
    return queued.filter(mine).slice(0, eventBatchLimit)
  }

  /*
   * 🔴 A PUSHED SNAPSHOT, BECAUSE §4.7 MUST NOT POLL.
   *
   * The teacher's chip needs a value that changes maybe four times an hour. A
   * hook polling `stats()` on a timer would be a wake-up every few seconds on a
   * classroom laptop — and it would quietly undo §4.6's rule that no timer runs
   * while the queue is empty. So the queue publishes, and `useSyncExternalStore`
   * subscribes.
   *
   * The snapshot is a frozen object replaced wholesale: React compares by
   * reference, and mutating it in place would render nothing.
   */
  let snapshot: OutboxStats = EMPTY_STATS
  const listeners = new Set<() => void>()

  function publish(next: Partial<OutboxStats>): void {
    const merged = { ...snapshot, ...next }
    if (
      merged.queued === snapshot.queued &&
      merged.failed === snapshot.failed &&
      merged.refused === snapshot.refused &&
      merged.sending === snapshot.sending
    ) {
      return // No change: notifying anyway is a re-render for nothing.
    }
    snapshot = merged
    for (const listener of listeners) listener()
  }

  /** Re-reads the counts from the store and publishes them. */
  async function refresh(extra: Partial<OutboxStats> = {}): Promise<void> {
    try {
      publish({
        queued: await store.count(),
        failed: (await store.failed()).length,
        refused,
        ...extra,
      })
    } catch {
      // A status read must never take the app down; the chip simply goes stale.
    }
  }

  /**
   * 🔴 SYNCHRONOUS, AND RETURNS `void`.
   *
   * Gameplay must not await the network *or* IndexedDB. An `await` on a storage
   * write inside a round is a frame-budget hazard on the lowest-spec school
   * laptop — the same machine already spending its budget on inference.
   *
   * ⚠️ The honest cost: an item is in memory for a millisecond or two before it
   * is durable, so a tab killed in that window loses it. The alternative puts a
   * storage round-trip in the frame loop of a game a four-year-old is playing.
   */
  function enqueue(input: EnqueueInput): void {
    try {
      assertNoBodyDerivedFields(input.body)
    } catch (error) {
      refused++
      /*
       * Dev: throw, at the call site that did it. Production: drop the item and
       * do not send. I1's failure direction is asymmetric — a lost event costs
       * one row on a report card; a transmitted landmark changes what the
       * product legally is.
       */
      void refresh()
      if (import.meta.env.DEV) throw error
      console.error(error)
      return
    }

    const item: OutboxItem = {
      kind: input.kind,
      path: input.path,
      body: input.body,
      context: 'device',
      createdAt: input.createdAt ?? clock.now(),
      attempts: 0,
      /*
       * 🔴 THE IDEMPOTENCY KEY IS MINTED HERE, ONCE, AND STORED WITH THE ITEM
       * (offline-sync.md §6). Every retry sends the same one, so a request that
       * landed but lost its response is a no-op the second time rather than a
       * second row on a report card. Synchronous and local — no cost to the
       * round.
       */
      ...(input.kind === 'event' ? { clientEventId: mintEventId() } : {}),
      // Who played it, read NOW — see `OutboxItem.owner`.
      ...ownerNow(),
    }

    const write = tail
      .then(async () => {
        await enforceCap(item.kind)
        await store.add(item)
      })
      // A storage failure must not surface into a round. The child keeps
      // playing; this device loses that write.
      .catch((error: unknown) => {
        console.error('outbox: enqueue failed', error)
      })
      .then(() => refresh())
    tail = write

    /*
     * Best-effort immediate send (§4) — deliberately NOT part of `tail`, and
     * deliberately through the clock.
     *
     * `whenSettled()` means "the item is durable", which is the only thing a
     * caller could reasonably wait for. Folding the flush into it would make it
     * mean "durable AND the network has answered", which is precisely the wait
     * this whole module exists to remove.
     *
     * Going through `clock.setTimeout(…, 0)` rather than a microtask also keeps
     * the send off the tail of the frame that produced the event: a jump lands,
     * the round advances, and the request goes out on the next task.
     */
    void write.then(() => {
      clock.setTimeout(() => {
        void flush()
      }, 0)
    })

    schedule()
  }

  /**
   * §5: *"Queue size cap 500 — beyond that, drop oldest **events** (never
   * progress) and log."*
   *
   * 🔴 EXTENDED TO SESSIONS BY THE SAME LOGIC (§4.3 added the kind). Dropping a
   * session is worse than dropping progress: it is the **container**, so every
   * event referencing it would be rejected as an unknown session and discarded
   * one by one — a whole sitting lost to reclaim one row.
   */
  async function enforceCap(incoming: OutboxKind): Promise<void> {
    if ((await store.count()) < QUEUE_CAP) return

    const oldestEvent = (await store.all()).find((i) => i.kind === 'event')
    if (oldestEvent?.id !== undefined) {
      console.warn('outbox: queue full, dropping oldest event')
      await store.remove(oldestEvent.id)
      return
    }

    // Nothing droppable: a queue of 500 sessions and progress snapshots. Going
    // over the cap is the lesser evil — the alternative is dropping the child's
    // coins or a session other items depend on.
    console.warn(`outbox: queue full and nothing droppable; keeping ${incoming}`)
  }

  /**
   * One FIFO pass. Single-flight, because four triggers can fire in the same
   * tick when the wifi returns and the tab regains focus together — and two
   * loops over one queue would send the same item twice and interleave order.
   *
   * 🔴 CONSECUTIVE EVENTS TRAVEL TOGETHER; EVERYTHING ELSE TRAVELS ALONE. A
   * drained queue used to be one round trip per event — a weekend backlog was
   * a hundred and fifty requests per laptop, all at the moment the wifi came
   * back. Now the run of events at the head of the queue goes as one
   * `POST /events/batch`, up to `EVENT_BATCH_MAX`. A session or a progress
   * snapshot ENDS the run and is sent on its own, exactly as before, so FIFO is
   * untouched: nothing is reordered, a batch is just several adjacent items
   * leaving at once.
   */
  async function flush(): Promise<void> {
    if (flushing) return
    if (clock.now() < backoffUntil) {
      // Not now — but make sure something wakes us when the window ends. See
      // `schedule` on why this used to be able to stall for good.
      schedule()
      return
    }

    const token = getToken()
    // Nothing can be sent before registration (§4.5). Not a failure: items wait,
    // and ensureDevice()'s post-register hook calls back here.
    if (!token) return

    flushing = true
    void refresh({ sending: true })
    try {
      const player = getPlayer()
      for (;;) {
        const window = await nextFor(player)
        const head = window[0]
        if (!head?.id) break

        const run = eventRun(window)
        const step = run.length > 1 ? await deliverBatch(run, token) : await deliverOne(head, token)
        if (step === 'stop') return
      }

      // A cycle that stopped failing: reset. Not on a single successful item —
      // the backoff is per cycle (§5).
      consecutiveFailedCycles = 0
      backoffUntil = 0
    } finally {
      flushing = false
      void refresh({ sending: false })
      schedule()
    }
  }

  /**
   * The events at the head of `window` that can share one batch: consecutive,
   * addressed to `/events`, and inside the body budget. Always at least the
   * head itself when the head is one.
   */
  function eventRun(window: readonly OutboxItem[]): OutboxItem[] {
    const run: OutboxItem[] = []
    let chars = 0
    for (const item of window) {
      if (!isBatchable(item)) break
      // `?? ''`: `JSON.stringify(undefined)` is undefined, whatever its type says.
      const json = JSON.stringify(wireBody(item)) as string | undefined
      const size = (json ?? '').length + (item.clientEventId ? 0 : EVENT_ID_CHARS)
      if (run.length > 0 && chars + size > EVENT_BATCH_MAX_CHARS) break
      run.push(item)
      chars += size + 1
    }
    return run
  }

  /** One item, on its own route — sessions, progress, and a lone event. */
  async function deliverOne(queued: OutboxItem, token: string): Promise<Step> {
    const item = await withEventId(queued)
    const id = item.id as number

    try {
      await send(item, token, getPlayer())
      await store.remove(id)
      if (isBatchable(item)) eventBatchLimit = Math.min(EVENT_BATCH_MAX, eventBatchLimit * 2)
      return 'continue'
    } catch (error) {
      if (!isRetryable(error)) {
        /*
         * 🔴 THE RULE THAT MATTERS MOST (§5). "A 400/403 will never succeed
         * on retry — discard the item, don't poison the queue." A single
         * malformed item retried forever behind a FIFO queue blocks every
         * subsequent write: the queue stops being a buffer and becomes a
         * wall.
         *
         * A 409 on a session-create lands here too, and correctly: the row
         * exists (the response was lost), so its events will still land.
         */
        console.warn(`outbox: discarding ${item.kind} after a permanent failure`)
        await store.remove(id)
        return 'continue'
      }

      // Retryable: the request was fine, the moment was not.
      return retryLater(item, error, 1)
    }
  }

  /**
   * Two or more consecutive events in one request.
   *
   * 🔴 A 2xx DELETES EXACTLY WHAT WAS SENT. The API answers 202 for the whole
   * batch — every event accepted or already present — so the ids in `run` are
   * removed and nothing else: an event queued while the request was in flight
   * is not part of it, and "drop the head N" would delete it unsent.
   *
   * 🔴 A 4xx IS SPLIT, NOT DISCARDED. The API is all-or-nothing, so a 400 means
   * at least one event is bad and says nothing about the others. Discarding the
   * batch would throw away up to ninety-nine good events with the one bad one;
   * retrying it would rebuild the wall §5 forbids. So it halves and delivers
   * each half the same way, until the bad event is alone — where `deliverOne`
   * applies the per-item rule it always has and discards it. One bad event in a
   * hundred costs about a dozen requests, and every other event lands.
   */
  async function deliverBatch(run: readonly OutboxItem[], token: string): Promise<Step> {
    const items = await withEventIds(run)

    try {
      await sendBatch(items, token)
      await store.removeMany(items.map((item) => item.id as number))
      eventBatchLimit = Math.min(EVENT_BATCH_MAX, eventBatchLimit * 2)
      return 'continue'
    } catch (error) {
      if (isRetryable(error)) return retryLater(items[0], error, items.length)

      /*
       * ⚠️ A REFUSAL OF THE REQUEST RATHER THAN OF ANY EVENT IN IT, so splitting
       * would only repeat it ~2N times. 404/405: the API predates the batch
       * route — a web build deployed ahead of its API, or an API rolled back —
       * and every one of these events is still welcome on `POST /events`. 401:
       * the token, not the body; one item at a time is exactly what the queue
       * did with a 401 before batching existed.
       */
      if (error instanceof ApiError && [401, 404, 405].includes(error.status)) {
        for (const item of items) {
          if ((await deliverOne(item, token)) === 'stop') return 'stop'
        }
        return 'continue'
      }

      const middle = Math.ceil(items.length / 2)
      if ((await deliverEvents(items.slice(0, middle), token)) === 'stop') return 'stop'
      return deliverEvents(items.slice(middle), token)
    }
  }

  function deliverEvents(items: readonly OutboxItem[], token: string): Promise<Step> {
    return items.length > 1 ? deliverBatch(items, token) : deliverOne(items[0], token)
  }

  /**
   * A retryable failure of the item at the head of the queue — alone, or
   * leading a batch.
   *
   * 🔴 STOP ON FIRST FAILURE — this is what preserves ordering. "Skip it and
   * try the next one" would let a progress write overtake the events it
   * summarises.
   *
   * 🔴 ONLY THE HEAD IS CHARGED AN ATTEMPT, as it was before batching. The cap
   * exists so one unsendable item cannot block the queue forever; charging
   * every event in a failed batch would march a hundred of them into `failed`
   * together after one long outage.
   *
   * ⚠️ AND THE NEXT BATCH IS A QUARTER OF THIS ONE. A retryable failure usually
   * means the moment (offline, a 502), but it can mean an event the API chokes
   * on with a 500 every time — and then every batch containing it fails, and
   * the head is charged for an event it does not contain. Shrinking finds it:
   * a hundred, then twenty-five, then six, then one, so within a few cycles
   * the innocent head travels alone and lands, and the bad event reaches the
   * head of the queue and burns its own ten attempts as it always would. Every
   * success doubles the limit back. A 429 is the exception: it is about the
   * number of requests, and smaller batches would only mean more of them.
   */
  async function retryLater(head: OutboxItem, error: unknown, sent: number): Promise<Step> {
    if (sent > 1 && !(error instanceof ApiError && error.status === 429)) {
      eventBatchLimit = Math.max(1, Math.floor(sent / 4))
    }

    const attempts = head.attempts + 1
    if (attempts >= MAX_ATTEMPTS) {
      // The cap exists so one unsendable item cannot block the queue
      // forever. It moves aside and the loop CONTINUES.
      await store.fail({ ...head, attempts })
      return 'continue'
    }

    await store.update({ ...head, attempts })
    consecutiveFailedCycles++
    backoffUntil = clock.now() + fullJitterBackoff(consecutiveFailedCycles, random)
    return 'stop'
  }

  /**
   * 🔴 A KEY FOR AN EVENT AN OLDER BUILD QUEUED — WRITTEN BACK BEFORE IT IS SENT.
   *
   * Items queued before `clientEventId` existed have none. Sending them keyless
   * would leave exactly the duplicate this fixes; minting one per send would be
   * worse, a fresh key per retry. So the key is minted once and persisted
   * first: if the response is lost, the retry reads the item back with the
   * same key, and the server recognises it.
   */
  async function withEventId(item: OutboxItem): Promise<OutboxItem> {
    if (item.kind !== 'event' || item.clientEventId) return item
    const keyed = { ...item, clientEventId: mintEventId() }
    await store.update(keyed)
    return keyed
  }

  async function withEventIds(items: readonly OutboxItem[]): Promise<OutboxItem[]> {
    const keyed: OutboxItem[] = []
    for (const item of items) keyed.push(await withEventId(item))
    return keyed
  }

  /**
   * The timer trigger — *"while items remain"*. A timer on an idle device is a
   * wake-up for nothing, on a laptop sitting in a classroom.
   *
   * 🔴 IT WAKES AT THE END OF THE BACKOFF, NOT ON A FIXED 30 s TICK. Two reasons,
   * and the first is the one jitter depends on:
   *
   *  1. A retry happens when something TRIGGERS a flush after the backoff has
   *     passed. With the timer armed 30 s after a failure, thirty laptops that
   *     failed together all retried together at +30 s whatever their jittered
   *     windows said — the lockstep simply moved from the backoff to the timer.
   *     Waking at `backoffUntil` makes the draw the retry time.
   *  2. A backoff longer than 30 s used to end the timer for good: it fired
   *     inside the window, the flush returned early without rescheduling, and
   *     an idle laptop's queue then waited for the next focus or online event —
   *     *"which on a device left open all morning may be hours"* (§4).
   *
   * With no backoff in force it is the plain 30 s periodic trigger §4 added.
   */
  function schedule(): void {
    void store.count().then((queued) => {
      if (queued === 0) {
        disarm()
        return
      }
      const now = clock.now()
      const at = backoffUntil > now ? backoffUntil : now + PERIODIC_MS
      // Already waking at or before then — leave it.
      if (timer !== null && timerAt <= at) return
      disarm()
      timerAt = at
      timer = clock.setTimeout(() => {
        timer = null
        void flush()
      }, at - now)
    })
  }

  function disarm(): void {
    if (timer !== null) clock.clearTimeout(timer)
    timer = null
  }

  async function stats(): Promise<OutboxStats> {
    await refresh()
    return snapshot
  }

  /**
   * The teacher's "retry" (§4.7) — the only way back out of the `failed` store.
   *
   * Never throws: it is a tap in the classroom chrome, not a round, but a status
   * control that can take the screen down is worse than one that quietly does
   * nothing.
   */
  async function retryFailed(): Promise<number> {
    try {
      const moved = await store.restoreFailed()
      await refresh()
      if (moved > 0) void flush()
      return moved
    } catch (error) {
      console.error('outbox: retry failed', error)
      return 0
    }
  }

  return {
    enqueue,
    flush,
    stats,
    retryFailed,
    /** For `useSyncExternalStore` — synchronous, and replaced by reference. */
    getSnapshot: (): OutboxStats => snapshot,
    subscribe: (listener: () => void): (() => void) => {
      listeners.add(listener)
      // The first subscriber inherits whatever the queue already holds — a tab
      // reopened with a queue from yesterday must show the chip immediately.
      void refresh()
      return () => listeners.delete(listener)
    },
    /** Tests only — gameplay never awaits a write. */
    whenSettled: (): Promise<unknown> => tail,
    stop: disarm,
  }
}

export type Outbox = ReturnType<typeof createOutbox>

/** A queued event the batch route can carry. */
function isBatchable(item: OutboxItem): boolean {
  return item.kind === 'event' && item.path === EVENTS_PATH
}

/**
 * The body as it goes on the wire: the caller's, plus the queue's key.
 *
 * The key lives on the item rather than in the stored body (see `types.ts`),
 * so it is merged here, at the one point both senders share.
 */
function wireBody(item: OutboxItem): unknown {
  const { body, clientEventId } = item
  if (!clientEventId || typeof body !== 'object' || body === null || Array.isArray(body)) return body
  return { ...(body as Record<string, unknown>), clientEventId }
}

/**
 * The real sender.
 *
 * 🔴 THE PATH AND THE BEARER ARE RESOLVED HERE, NOT AT ENQUEUE TIME. A device
 * may play before it has ever registered (offline-sync.md §8), so at enqueue
 * time there is no `playerId` to build `/progress/<id>` with and no token to
 * send it with. Resolving late is what makes "offline at first launch → games
 * still run, everything queues" literally true.
 */
async function sendOverHttp(item: OutboxItem, token: string, playerId: string | null): Promise<void> {
  let path = item.path
  if (path.includes('{playerId}')) {
    if (!playerId) {
      // Unregistered: nothing to address it to yet. Retryable on purpose — this
      // resolves itself the moment registration succeeds.
      throw new ApiError(0, 'No playerId yet')
    }
    path = path.replace('{playerId}', encodeURIComponent(playerId))
  }

  await request(path, { method: 'POST', body: wireBody(item), context: 'device', token })
}

/**
 * The real batch sender — `POST /events/batch`, `{ events: [...] }`.
 *
 * No `{playerId}` to resolve: the API takes the player from the token, for a
 * batch exactly as for one event.
 */
async function sendBatchOverHttp(items: readonly OutboxItem[], token: string): Promise<void> {
  await request(EVENTS_BATCH_PATH, {
    method: 'POST',
    body: { events: items.map(wireBody) },
    context: 'device',
    token,
  })
}
